
import Vue from 'vue';
import Component from 'vue-class-component';
import {Getter, Action} from 'vuex-class';
import {AppState, UyapStatusMessage} from '@/types';

interface PrimaryActionConfig {
  label: string;
  icon: string;
  color: string;
  outlined: boolean;
}

@Component({})
export default class UyapStatusCard extends Vue {
  @Getter isUyapConnected!: boolean;
  @Getter uyapStatusMessage!: UyapStatusMessage | null;
  @Getter healthCheckRetryCount!: number;
  @Getter isHealthChecking!: boolean;
  @Getter availableCardList!: any[];
  @Action connectToUyap!: () => Promise<void>;
  @Action checkUyapSession!: () => Promise<void>;
  @Action('availableCards') fetchAvailableCards!: (payload?: { silent?: boolean }) => Promise<void>;

  isRefreshing = false;
  isCardRefreshing = false;
  private readonly defaultHealthRetryLimit = 3;

  get hasAvailableCard(): boolean {
    return Array.isArray(this.availableCardList) && this.availableCardList.length > 0;
  }

  // Kart yokken "Bağlan" tıklanamaz; bağlıyken buton "Yenile" olduğu için etkilenmez.
  get isPrimaryDisabled(): boolean {
    return !this.isUyapConnected && !this.hasAvailableCard;
  }

  // Kart listesi otomatik tazelenmediği için, kart takıldığında kullanıcıya
  // listeyi yenileyip "Bağlan"ı aktifleştirecek bir yol sunulur.
  get showCardRefresh(): boolean {
    return !this.isUyapConnected && !this.hasAvailableCard;
  }

  get statusLabel(): string {
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'Kontrol Ediliyor';
    return this.isUyapConnected ? 'Bağlı' : 'Bağlı Değil';
  }

  get statusIcon(): string {
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'mdi-progress-clock';
    return this.isUyapConnected ? 'mdi-check-circle' : 'mdi-alert-circle';
  }

  get statusColor(): string {
    const type = this.uyapStatusMessage?.type;
    if (type === 'error') return '#ef5350';
    if (type === 'success' || this.isUyapConnected) return 'success darken-2';
    if (type === 'info') return '#E1893F';
    return 'error darken-2';
  }

  get statusIconColor(): string {
    const type = this.uyapStatusMessage?.type;
    if (type === 'success' || this.isUyapConnected) return '#4caf50';
    if (type === 'error') return '#ff5252';
    if (type === 'info') return '#f59e0b';
    return '#facc15';
  }

  get statusIconBubbleColor(): string {
    const type = this.uyapStatusMessage?.type;
    if (type === 'success' || this.isUyapConnected) return 'rgba(76, 175, 80, 0.15)';
    if (type === 'error') return 'rgba(244, 67, 54, 0.15)';
    if (type === 'info') return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(250, 204, 21, 0.15)';
  }

  get statusTextColor(): string {
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'black';
    return 'white';
  }

  get statusDescription(): string {
    const message = this.uyapStatusMessage?.text;
    if (!message) {
      if (this.isUyapConnected) {
        return 'UYAP bağlantısı aktif ve hazır.';
      }
      if (!this.hasAvailableCard) {
        return 'E-imza kartı takılı değil. Kartı taktıktan sonra "Kartları Yenile" ile kontrol edin.';
      }
      return 'Giriş yapılmadı veya oturum süresi doldu.';
    }
    return this.normalizeMessage(message);
  }

  private normalizeMessage(raw: string): string {
    let value = raw.trim();
    value = value.replace(/^error:\s*/i, '');
    if (!value) return 'UYAP bağlantısı başarısız.';

    const lower = value.toLowerCase();
    if (lower.includes('kart') || lower.includes('takılı') || lower.includes('takili')) {
      return 'İmzacı takılı değil.';
    }
    if (lower.includes('timeout') || lower.includes('zaman')) {
      return 'UYAP cevap vermedi, lütfen tekrar deneyin.';
    }
    if (lower.includes('uyap bağlantısı yapılamadı')) {
      return 'UYAP bağlantısı yapılamadı. Lütfen tekrar deneyin.';
    }
    if (lower.includes('bekleyen') && lower.includes('giriş')) {
      return "UYAP'ta bekleyen bir giriş isteği bulunuyor. Tarayıcıdan isteği onaylayın veya iptal edin.";
    }
    return value;
  }

  get maxHealthCheckRetries(): number {
    const limit = (this.$store.state as AppState).healthCheckMaxRetries;
    return typeof limit === 'number' ? limit : this.defaultHealthRetryLimit;
  }

  get heartbeatIcon(): string {
    return this.isHealthChecking ? 'mdi-pulse' : 'mdi-heart-off';
  }

  get heartbeatIconColor(): string {
    return this.isHealthChecking ? 'green darken-1' : 'amber darken-2';
  }

  get heartbeatLabel(): string {
    return this.isHealthChecking ? 'Otomatik izleme aktif' : 'Otomatik izleme kapalı';
  }

  get primaryAction(): PrimaryActionConfig {
    if (this.isUyapConnected) {
      return {
        label: 'Yenile',
        icon: 'mdi-refresh',
        color: 'grey darken-3',
        outlined: true,
      };
    }

    return {
      label: 'Bağlan',
      icon: 'mdi-link-variant',
      color: 'success darken-1',
      outlined: false,
    };
  }

  async handleCardRefresh() {
    if (this.isCardRefreshing) return;
    this.isCardRefreshing = true;
    const previousMessage = this.uyapStatusMessage;
    try {
      await this.fetchAvailableCards({ silent: true });
      // Fetch başarılı ama liste boşsa liste otoritedir: asıl sorun kart eksikliği.
      // availableCards hata durumunda YENİ bir mesaj basar (mesaj objesi değişir);
      // o durumda ona dokunma. Mesaj değişmediyse (eski/bayat), doğru teşhisi yaz.
      if (!this.hasAvailableCard && this.uyapStatusMessage === previousMessage) {
        // Metin 'kart' kelimesini içermeli: availableCards action'ı kart bulununca
        // bu mesajı 'kart'/'imzacı'/'program' geçen metinlere bakarak temizliyor.
        this.$store.commit('setUyapStatusMessage', {
          type: 'error',
          text: 'E-imza kartı takılı değil.',
          timestamp: Date.now()
        });
      }
    } finally {
      this.isCardRefreshing = false;
    }
  }

  // Time: O(1), Space: O(1) — tek aksiyon tetikleyicisi.
  async handlePrimaryAction() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    try {
      if (this.isUyapConnected) {
        await this.checkUyapSession();
      } else {
        await this.connectToUyap();
      }
    } catch (error) {
      console.error('UYAP bağlantı işlemi başarısız.', error);
      this.$toast?.error?.('UYAP bağlantısı sağlanamadı. Lütfen tekrar deneyin.');
    } finally {
      this.isRefreshing = false;
    }
  }
}
